import {
  API_ROUTES,
  API_ROUTE_PREFIX_HTTPS,
  API_ROUTE_V2_PREFIX_HTTPS,
  EditTravellerPayload,
  EndorsementResponse,
  getCmpHeaderValueForClient,
  ICheckCheckoutEligibilityRequestParams,
  IClaimBenefitRequestParams,
  IEditTravellersRequestParams,
  IGetNextPageRouteRequestParams,
  IGetSuggestionsInfoRequestParams,
  IOrderDetailsRequestParams,
  IPageRequestParams,
  resolveCmpForApiRequest,
} from "@tm/api-utils";
import {
  CONTEXT,
  LOB,
  PARTNER,
  PartnerType,
  PLATFORM,
} from "@tm/isomorphic-utils";
import {
  getHTTPSRoutePrefixOnClientSide,
  getLobOnClientSide,
  getPartnerOnClientSide,
  getPathPartnerOnClientSide,
  setURLQueryParams,
} from "../utils";
import { getDeviceIdForClient } from "../utils/getDeviceId";
import { API_HTTPS } from "./config";

export const paymentCheckout = async (reqData: any): Promise<any> => {
  try {
    const { partner = getPartnerOnClientSide(), data, query } = reqData;
    const params = {
      context: CONTEXT.STANDALONE_V2,
      ins_new_flow: true,
      lob: getLobOnClientSide(partner),
      itineraryId: data?.query?.itineraryId,
    };
    const urlParams = setURLQueryParams(params);
    const dvid = getDeviceIdForClient();

    const apiRoutePrefix = getHTTPSRoutePrefixOnClientSide(partner);

    const pathPartner = getPathPartnerOnClientSide(partner);

    const response = await API_HTTPS.post(
      `${apiRoutePrefix}/${pathPartner}/${API_ROUTES.PAYMENT_CHECKOUT}${
        urlParams ? `?${urlParams}` : ""
      }`,
      data,
      {
        headers: {
          cmp: getCmpHeaderValueForClient(query),
          dvid: dvid || "",
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error", { error });
    throw error;
  }
};

export const sendPolicyEmail = async (data: any): Promise<any> => {
  try {
    const { bookingId, partner, query } = data;
    const urlParams = setURLQueryParams({ bookingID: bookingId, ...query });
    const dvid = getDeviceIdForClient();
    const pathPartner = getPathPartnerOnClientSide(partner);
    const response = await API_HTTPS.post(
      `${API_ROUTE_PREFIX_HTTPS}/${pathPartner}/${API_ROUTES.TRIGGER_EMAIL}${
        urlParams ? `?${urlParams}` : ""
      }`,
      {
        headers: {
          cmp: getCmpHeaderValueForClient(query),
          dvid: dvid || "",
        },
      }
    );
    return response?.data;
  } catch (error: any) {
    console.log("Error", { error: error.message });
    throw error;
  }
};

export const editInsuranceDetails = async (
  reqData: IEditTravellersRequestParams
): Promise<any> => {
  try {
    const {
      bookingID,
      partner = getPartnerOnClientSide(),
      data = [],
      query,
    } = reqData;

    if (!bookingID)
      throw "editInsuranceDetails: Booking id not present or invalid";

    const urlParams = setURLQueryParams({ bookingID });
    const dvid = getDeviceIdForClient();
    const pathPartner = getPathPartnerOnClientSide(partner);

    const response = await API_HTTPS.put(
      `${API_ROUTE_PREFIX_HTTPS}/${pathPartner}/${
        API_ROUTES.EDIT_INSURANCE_DETAILS
      }${urlParams ? `?${urlParams}` : ""}`,
      data,
      {
        timeout: 30000,
        headers: {
          cmp: getCmpHeaderValueForClient(query),
          dvid: dvid || "",
        },
      }
    );
    return response?.data;
  } catch (error) {
    console.error("Error", { error });
    throw error;
  }
};

export const submitEndorsement = async (reqData: {
  bookingId: string;
  payload: EditTravellerPayload;
  partner?: string;
  query?: Record<string, string | string[] | undefined>;
}): Promise<EndorsementResponse> => {
  try {
    const {
      partner = getPartnerOnClientSide(),
      bookingId,
      payload,
      query,
    } = reqData;
    const dvid = getDeviceIdForClient();
    const pathPartner = getPathPartnerOnClientSide(
      partner as Parameters<typeof getPathPartnerOnClientSide>[0]
    );
    const urlParams = setURLQueryParams({ bookingId, ...query });
    const response = await API_HTTPS.post(
      `${API_ROUTE_PREFIX_HTTPS}/${pathPartner}/${
        API_ROUTES.EDIT_TRAVELLER_V2
      }${urlParams ? `?${urlParams}` : ""}`,
      payload,
      {
        headers: {
          cmp: getCmpHeaderValueForClient(query),
          dvid: dvid || "",
        },
      }
    );

    return response?.data;
  } catch (error) {
    console.error("Error submitting endorsement", { error });
    throw error;
  }
};

export const sendPolicyEmailV2 = async (data: any): Promise<any> => {
  try {
    const { bookingId, partner, query } = data;
    const urlParams = setURLQueryParams({ bookingId, ...query });
    const dvid = getDeviceIdForClient();
    const pathPartner = getPathPartnerOnClientSide(partner);
    const response = await API_HTTPS.post(
      `${API_ROUTE_PREFIX_HTTPS}/${pathPartner}/${
        API_ROUTES.SEND_POLICY_EMAIL
      }${urlParams ? `?${urlParams}` : ""}`,
      {
        headers: {
          cmp: getCmpHeaderValueForClient(query),
          dvid: dvid || "",
        },
      }
    );
    return response?.data;
  } catch (error: any) {
    console.log("Error", { error: error.message });
    throw error;
  }
};

export const getNextPageRouteDetails = async (
  reqData: IGetNextPageRouteRequestParams
) => {
  const {
    partner = PARTNER.MMT,
    query,
    isV2Endpoint = false,
    ...data
  } = reqData;
  const dvid = getDeviceIdForClient();
  const params = {
    lob: getLobOnClientSide(partner),
    platform: PLATFORM.WEB,
    context: CONTEXT.STANDALONE_V2,
    isCountryCodesSupported: true.toString(),
    partner,
    ...query,
    ...(isV2Endpoint && { authV2: true.toString() }),
  };

  const urlParams = new URLSearchParams(params).toString();

  let url;
  if (partner === PARTNER.MYPARTNER || partner === PARTNER.MYBIZ) {
    url = `${API_ROUTE_V2_PREFIX_HTTPS}/mmt/${API_ROUTES.GET_NEXT_PAGE_ROUTE}?${urlParams}`;
  } else if (isV2Endpoint) {
    url = `${API_ROUTE_V2_PREFIX_HTTPS}/${partner}/${API_ROUTES.GET_NEXT_PAGE_ROUTE}?${urlParams}`;
  } else {
    url = `${API_ROUTE_PREFIX_HTTPS}/${partner}/${API_ROUTES.GET_NEXT_PAGE_ROUTE}?${urlParams}`;
  }

  const response = await API_HTTPS.post(url, data, {
    headers: {
      dvid: dvid || "",
      cmp: getCmpHeaderValueForClient(query),
    },
  });

  if (response.data?.data?.nextPageRouteConfig) {
    return {
      responseStatus: response.data?.responseStatus,
      data: {
        ...(response.data?.data || {}),
        experiment: response.data?.data?.experiment || "",
      },
      nextPageRouteConfig: response.data?.data?.nextPageRouteConfig || "",
    };
  }
  return {
    ...response.data,
    data: {
      ...(response.data?.data || {}),
      experiment: response.data?.experiment || "",
    },
    nextPageRouteConfig: response.data?.nextPageRouteConfig || "",
  };
};

export const hitRetailConsentMyPartner = async (
  reqData: IPageRequestParams
) => {
  const { partner = PARTNER.MYPARTNER, query, ...data } = reqData;
  const dvid = getDeviceIdForClient();
  const params = {
    lob: getLobOnClientSide(partner),
    platform: PLATFORM.WEB,
    context: CONTEXT.STANDALONE_V2,
    isCountryCodesSupported: true.toString(),
    partner,
    ...query,
  };

  const urlParams = new URLSearchParams(params).toString();
  const url = `${API_ROUTE_PREFIX_HTTPS}/${API_ROUTES.HIT_RETAIL_CONSENT}?${urlParams}`;
  const response = await API_HTTPS.post(url, data, {
    headers: { dvid: dvid || "" },
  });
  return response.data;
};

export const getOrdersMyPartner = async (reqData: IPageRequestParams) => {
  const { partner = PARTNER.MYPARTNER, query, ...data } = reqData;
  const dvid = getDeviceIdForClient();
  const params = {
    lob: getLobOnClientSide(partner),
    platform: PLATFORM.WEB,
    context: CONTEXT.STANDALONE_V2,
    isCountryCodesSupported: true.toString(),
    partner,
    ...query,
  };

  const urlParams = new URLSearchParams(params).toString();
  const url = `${API_ROUTE_PREFIX_HTTPS}/${API_ROUTES.GET_ORDERS_RETAIL_MYPARTNER}?${urlParams}`;
  const response = await API_HTTPS.get(url, {
    params: data,
    headers: { dvid: dvid || "" },
  });
  return response.data;
};

export const checkCheckoutEligibility = async (
  reqData: ICheckCheckoutEligibilityRequestParams
) => {
  const { partner = PARTNER.MMT, query, ...data } = reqData;
  const dvid = getDeviceIdForClient();
  const params = {
    lob: LOB.STANDALONE,
    platform: PLATFORM.WEB,
    context: CONTEXT.STANDALONE_V2,
    isCountryCodesSupported: true.toString(),
    partner,
    ...query,
  };

  const urlParams = new URLSearchParams(params).toString();
  const url = `${API_ROUTE_PREFIX_HTTPS}/${partner}/${API_ROUTES.CHECK_CHECKOUT_ELIGIBILITY}?${urlParams}`;

  const response = await API_HTTPS.post(url, data, {
    headers: { dvid: dvid || "" },
  });
  return response.data;
};

export const getSuggestionsInfo = async (
  reqData: IGetSuggestionsInfoRequestParams
) => {
  const { query, ...data } = reqData;

  const params = {
    lob: LOB.STANDALONE_MYBIZ,
    platform: PLATFORM.WEB,
    context: CONTEXT.STANDALONE_V2,
    partner: PARTNER.MYBIZ,
    ...query,
  };
  const dvid = getDeviceIdForClient();

  const urlParams = new URLSearchParams(params).toString();
  const url = `${API_ROUTE_V2_PREFIX_HTTPS}/mmt/${API_ROUTES.GET_SUGGESTIONS_INFO}?${urlParams}`;

  const response = await API_HTTPS.post(url, data, {
    headers: { dvid: dvid || "" },
  });
  return response.data;
};

export const getPolicyStatus = async (
  reqData: IOrderDetailsRequestParams
): Promise<{
  responseStatus: string;
  message: string;
  data: {
    policyStatus:
      | "GENERATING_POLICY"
      | "GENERATING_POLICY_SUCCESS"
      | "GENERATING_POLICY_FAILED"
      | "UPDATING_POLICY"
      | "UPDATING_POLICY_SUCCESS"
      | "UPDATING_POLICY_FAILED";
    continuePolling: boolean;
  };
}> => {
  try {
    const { partner = getPartnerOnClientSide(), bookingId, query } = reqData;
    const dvid = getDeviceIdForClient();
    const params = {
      lob: getLobOnClientSide(partner) || LOB.STANDALONE,
      bookingId,
      ...query,
    };

    const urlParams = setURLQueryParams(params);
    const pathPartner = getPathPartnerOnClientSide(partner);

    const response = await API_HTTPS.get(
      `${API_ROUTE_PREFIX_HTTPS}/${pathPartner}/${API_ROUTES.POLICY_STATUS_V2}${
        urlParams ? `?${urlParams}` : ""
      }`,
      {
        headers: {
          cmp: getCmpHeaderValueForClient(
            query,
            resolveCmpForApiRequest(reqData.cmp, query) || "fallback"
          ),
          dvid: dvid || "",
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error("Error fetching policy status", { error });
    throw error;
  }
};

export interface IListingClientSearchIntent {
  country: string[];
  departureDateTime: number;
  arrivalDateTime: number;
  numberOfTravellers: number;
  travelPurpose?: string;
  segment?: string;
  tripOption?: string;
  planType?: string;
  callerSource?: string;
}

export const getProductListingClient = async (
  searchIntent: IListingClientSearchIntent,
  query: Record<string, string> = {}
): Promise<any> => {
  try {
    const partner = getPartnerOnClientSide();
    const dvid = getDeviceIdForClient();
    const pathPartner = getPathPartnerOnClientSide(partner);

    const itineraryId =
      query["itineraryId"] ||
      (typeof window !== "undefined" &&
        sessionStorage.getItem("itineraryId")) ||
      "";

    const params = {
      lob: getLobOnClientSide(partner) || LOB.STANDALONE,
      platform: PLATFORM.WEB,
      context: CONTEXT.STANDALONE_V2,
      isCountryCodesSupported: "true",
      partner,
      authV2: "true",
      ...query,
      ...(itineraryId && { itineraryId }),
      countries: searchIntent.country?.join(","),
      startDate: searchIntent.departureDateTime,
      endDate: searchIntent.arrivalDateTime,
      travellers: searchIntent.numberOfTravellers,
      listingRevamp: query["listingRevamp"] || "true",
      ...(searchIntent.travelPurpose && {
        travelPurpose: searchIntent.travelPurpose,
      }),
      tripOption: searchIntent.tripOption || query["tripOption"],
      planType: searchIntent.planType || query["planType"],
      ...(searchIntent.callerSource && {
        callerSource: searchIntent.callerSource,
      }),
    };

    const urlParams = setURLQueryParams(params);
    const response = await API_HTTPS.post(
      `${API_ROUTE_V2_PREFIX_HTTPS}/${pathPartner}/${
        API_ROUTES.GET_PRODUCT_LISTING
      }${urlParams ? `?${urlParams}` : ""}`,
      {
        partner,
        userSearchIntent: searchIntent,
      },
      {
        headers: {
          cmp:
            (typeof window !== "undefined" && sessionStorage.getItem("cmp")) ||
            "fallback",
          dvid: dvid || "",
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching product listing", { error });
    throw error;
  }
};

export const getOrderDetailsV2 = async (
  reqData: IOrderDetailsRequestParams
): Promise<any> => {
  try {
    const { partner = getPartnerOnClientSide(), bookingId, query } = reqData;
    const dvid = getDeviceIdForClient();
    const sanitizedQuery = query
      ? (() => {
          const { action: _action, ...rest } = query;
          return rest;
        })()
      : query;
    const params = {
      lob: getLobOnClientSide(partner) || LOB.STANDALONE,
      bookingId,
      ...sanitizedQuery,
    };

    const urlParams = setURLQueryParams(params);
    const pathPartner = getPathPartnerOnClientSide(partner);

    const response = await API_HTTPS.get(
      `${API_ROUTE_PREFIX_HTTPS}/${pathPartner}/${API_ROUTES.ORDER_DETAIL_V2}${
        urlParams ? `?${urlParams}` : ""
      }`,
      {
        headers: {
          cmp: getCmpHeaderValueForClient(
            sanitizedQuery,
            resolveCmpForApiRequest(reqData.cmp, sanitizedQuery) || "fallback"
          ),
          dvid: dvid || "",
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error("Error fetching order details", { error });
    throw error;
  }
};

export const getCancelPolicyDetails = async (
  reqData: IOrderDetailsRequestParams
): Promise<any> => {
  try {
    const { partner = getPartnerOnClientSide(), bookingId, query } = reqData;
    const dvid = getDeviceIdForClient();
    const pathPartner = getPathPartnerOnClientSide(partner);
    const params = {
      lob: getLobOnClientSide(partner) || LOB.STANDALONE,
      bookingId,
      authV2: "true",
      ...query,
    };

    const urlParams = setURLQueryParams(params);

    const response = await API_HTTPS.get(
      `${API_ROUTE_PREFIX_HTTPS}/${pathPartner}/${
        API_ROUTES.TRAVELPROTECT_POST_SALES_CANCEL_INIT
      }${urlParams ? `?${urlParams}` : ""}`,
      {
        headers: {
          cmp: getCmpHeaderValueForClient(
            query,
            resolveCmpForApiRequest(reqData.cmp, query) || "fallback"
          ),
          dvid: dvid || "",
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error("Error fetching cancel policy details", { error });
    throw error;
  }
};

export interface CancelPolicySubmitPayload {
  bookingId: string;
  reasonId?: string;
  reasonText?: string;
  refundModeId?: string;
}

export interface CancelPolicySubmitRequest {
  payload: CancelPolicySubmitPayload;
  query?: Record<string, string>;
}

export interface CancelPolicySubmitResponse {
  success?: boolean;
  responseStatus?: string;
  message?: string;
  data?: {
    components?: Array<{ id?: string; data?: unknown }>;
  };
}

export const submitCancelPolicy = async (
  reqData: CancelPolicySubmitRequest
): Promise<CancelPolicySubmitResponse> => {
  try {
    const { payload, query } = reqData;
    const partner = getPartnerOnClientSide();
    const pathPartner = getPathPartnerOnClientSide(partner);
    const dvid = getDeviceIdForClient();
    const params = {
      lob: getLobOnClientSide(partner) || LOB.STANDALONE,
      authV2: "true",
      ...query,
    };
    const urlParams = setURLQueryParams(params);

    const response = await API_HTTPS.post(
      `${API_ROUTE_PREFIX_HTTPS}/${pathPartner}/${
        API_ROUTES.TRAVELPROTECT_POST_SALES_CANCEL_SUBMIT
      }${urlParams ? `?${urlParams}` : ""}`,
      payload,
      {
        headers: {
          dvid: dvid || "",
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error("Error submitting cancel policy", { error });
    throw error;
  }
};

export interface ClaimBenefitApiResponse {
  success?: boolean;
  message?: string;
  data?: {
    responseStatus?: string;
    message?: string;
    category?: string;
    staticPageData?: {
      headerTitle?: string;
      bodyComponents?: Array<{
        cardName?: string;
        data?: Record<string, unknown>;
      }>;
    };
    claimFlowData?: Record<string, unknown>;
  };
}

export const getClaimBenefitDetails = async (
  reqData: IClaimBenefitRequestParams
): Promise<ClaimBenefitApiResponse> => {
  try {
    const {
      partner = getPartnerOnClientSide(),
      bookingId,
      categoryId,
      nodeId,
      currency,
      query,
    } = reqData;
    const dvid = getDeviceIdForClient();
    const params: Record<string, string | number | boolean> = {
      lob: getLobOnClientSide(partner as PartnerType) || LOB.STANDALONE,
      bookingId,
    };

    if (categoryId) params["categoryId"] = categoryId;
    if (nodeId) params["nodeId"] = nodeId;
    if (currency) params["currency"] = currency;

    const urlParams = setURLQueryParams(params);
    const pathPartner = getPathPartnerOnClientSide(partner as PartnerType);

    const response = await API_HTTPS.get(
      `${API_ROUTE_PREFIX_HTTPS}/${pathPartner}/${API_ROUTES.CLAIM_BENEFIT}${
        urlParams ? `?${urlParams}` : ""
      }`,
      {
        headers: {
          cmp: getCmpHeaderValueForClient(
            query,
            resolveCmpForApiRequest(undefined, query) || "fallback"
          ),
          dvid: dvid || "",
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error("Error fetching claim benefit details", { error });
    throw error;
  }
};

export const fetchInvoiceList = async (
  invoiceUrl: string,
  bookingId: string
) => {
  const response = await API_HTTPS.post("/insurance/api/invoice-list", {
    invoiceUrl,
    bookingId,
  });

  return response?.data;
};
